// WorkflowVersionsModal — read-only version history, opened from the builder's
// "Version history" button (edit mode only, so a workflow id always exists).
// GET /workflows/:id/versions returns every version newest-first; each row
// expands to show that version's steps, their roles, and the response→routing
// table. Nothing here is editable — earlier versions are history, and the
// current one is edited in the builder underneath.
//
// This renders a Modal ON TOP of FormWorkflowModal's. Both portal to body, so
// the later one paints above; the TagsModal in FormWorkflowModal nests the same
// way. Escape closes both — a pre-existing consequence of Modal registering one
// document-level listener per open instance, not something introduced here.

import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Icon, IconButton, Modal } from '@/components/common';
import {
  WorkflowElementActionType,
  useGetWorkflowVersionsQuery,
  type WorkflowVersionElement,
} from '@/api/endpoints/workflowsApi';
import { fmtRelative } from '@/utils/formatDate';

interface WorkflowVersionsModalProps {
  open: boolean;
  /** The builder only renders its trigger in edit mode, so this is never empty. */
  workflowId: string;
  /** The workflow's current version number — the row that gets the "Current"
   *  pill. The response cannot say (every version comes back published), so the
   *  builder passes what /details reports. Null → the newest version wins. */
  currentVersion: number | null;
  onClose: () => void;
}

const WorkflowVersionsModal = ({
  open,
  workflowId,
  currentVersion,
  onClose,
}: WorkflowVersionsModalProps) => {
  const { t } = useTranslation();
  // Single-open accordion, per the design: opening one version closes the
  // previous. Holds the version NUMBER — the payload carries no ids.
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data, isLoading, isError } = useGetWorkflowVersionsQuery(workflowId, { skip: !open });

  // Every reopen starts fully collapsed, as the design's first state shows.
  useEffect(() => {
    if (open) setExpanded(null);
  }, [open]);

  const versions = data?.versions ?? [];
  // The response is already sorted newest-first, so the head is the newest.
  const currentNumber = currentVersion ?? versions[0]?.version ?? null;

  // What a response routes to. A `next_step` whose target the backend hasn't
  // named dashes rather than guessing: routing is arbitrary, and the sample
  // payload even carries a next_step off a version's LAST step, so the
  // following entry in the list is not a safe stand-in.
  const routeLabel = (el: WorkflowVersionElement) => {
    if (el.actionType === WorkflowElementActionType.BackToUser)
      return t('forms_workflow_route_back');
    if (el.actionType === WorkflowElementActionType.EndWorkflow)
      return t('forms_workflow_route_end');
    return el.nextStepTitle ?? <span className='workflow-versions__dash'>—</span>;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className='workflow-versions'
      icon='history'
      size='lg'
      title={t('forms_workflow_version_history')}
      subtitle={
        data && t('forms_workflow_versions_subtitle', { name: data.title, count: versions.length })
      }
      footer={
        <>
          <p className='workflow-versions__note'>
            <Icon name='info' size={16} aria-hidden='true' />
            {t('forms_workflow_versions_note')}
          </p>
          <Button onClick={onClose}>{t('button_close')}</Button>
        </>
      }
    >
      {isLoading ? (
        <p className='workflow-versions__state'>{t('text_loading')}</p>
      ) : isError ? (
        <p className='workflow-versions__state'>{t('error_generic')}</p>
      ) : versions.length === 0 ? (
        <p className='workflow-versions__state'>
          <Icon name='history' size={26} />
          {t('forms_workflow_versions_empty')}
        </p>
      ) : (
        <ul className='workflow-versions__list'>
          {versions.map((v) => {
            const isCurrent = v.version === currentNumber;
            const isOpen = expanded === v.version;
            return (
              <li
                key={v.version}
                className={[
                  'workflow-versions__row',
                  isCurrent && 'workflow-versions__row--current',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className='workflow-versions__head'>
                  {/* A bare version marker is data, not copy — no i18n key,
                      same as the builder's own "· vN" subtitle. */}
                  <span className='workflow-versions__label'>{`v${v.version}`}</span>
                  {/* Own class rather than a descendant selector: the expanded
                      body carries role Chips too, which must not be restyled. */}
                  {isCurrent && (
                    <Chip variant='neutral' className='workflow-versions__current'>
                      {t('forms_workflow_versions_current')}
                    </Chip>
                  )}
                  <span className='workflow-versions__meta'>
                    {fmtRelative(v.updatedAt, t)}
                    <span aria-hidden='true'> · </span>
                    {t('forms_workflow_steps', { count: v.steps.length })}
                  </span>
                  <IconButton
                    icon={isOpen ? 'chevron-up' : 'chevron-down'}
                    variant='outline'
                    aria-expanded={isOpen}
                    aria-label={t(
                      isOpen
                        ? 'forms_workflow_versions_collapse'
                        : 'forms_workflow_versions_expand',
                    )}
                    onClick={() => setExpanded(isOpen ? null : v.version)}
                  />
                </div>

                {isOpen && (
                  <div className='workflow-versions__body'>
                    {v.steps.map((step, i) => (
                      // No ids in the payload — a step is identified by its
                      // position within its version, which is also what the
                      // numbered badge shows.
                      <div key={i} className='workflow-versions__step'>
                        <div className='workflow-versions__step-head'>
                          <span className='workflow-versions__step-badge'>{i + 1}</span>
                          <span className='workflow-versions__step-title'>{step.title}</span>
                          <Chip variant='neutral' iconStart='user'>
                            {step.role}
                          </Chip>
                        </div>

                        {step.elements.length === 0 ? (
                          <p className='workflow-versions__empty'>
                            {t('forms_workflow_no_responses')}
                          </p>
                        ) : (
                          <div className='workflow-versions__grid'>
                            <span className='workflow-versions__col-label'>
                              {t('forms_workflow_versions_response')}
                            </span>
                            <span className='workflow-versions__col-label'>
                              {t('forms_workflow_versions_routes')}
                            </span>
                            {step.elements.map((el, j) => (
                              // Two cells per element rather than a nested row,
                              // so both columns align across every response.
                              <Fragment key={j}>
                                <span className='workflow-versions__response'>{el.title}</span>
                                <span className='workflow-versions__route'>{routeLabel(el)}</span>
                              </Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
};

export default WorkflowVersionsModal;
